import {
   createContext,
   useState,
   useMemo,
   PropsWithChildren,
   useContext,
   useCallback,
} from 'react';
import { MinimalDisclosure } from '../Logic/minimalDisclosure';
import filter from 'lodash/fp/filter';
import sortBy from 'lodash/fp/sortBy';
import { HotSpotItem, useDataContext } from './dataContext';

function useDisclosure(options?: { defaultIsOpen?: boolean }) {
   const [isOpen, setIsOpen] = useState(options?.defaultIsOpen ?? false);
   const onOpen = useCallback(() => setIsOpen(true), []);
   const onClose = useCallback(() => setIsOpen(false), []);
   const onToggle = useCallback(() => setIsOpen(prev => !prev), []);
   return { isOpen, onOpen, onClose, onToggle };
}

const noop = () => {};

const searchContextDefaults = {
   searchText: '',
   setSearchText: noop as (_searchText: string) => void,
   disclosure: {
      isOpen: false,
      onOpen: noop,
      onClose: noop,
      onToggle: noop,
   } as MinimalDisclosure,
   filteredData: [] as HotSpotItem[],
};

function filterData<T extends HotSpotItem>(
   data: T[],
   searchText: string,
   filterFn: SearchContextProps<T>['searchDataPoint']
) {
   return filter((datapoint: T) => filterFn(searchText, datapoint), data);
}

type SearchContextProps<T extends HotSpotItem> = {
   initialSearchText?: string;
   searchDataPoint: (_searchText: string, _hotSpotItem: T) => boolean;
};

const SearchContext = createContext(searchContextDefaults);

type SearchContextType<T extends HotSpotItem> = {
   searchText: string;
   setSearchText: (_searchText: string) => void;
   disclosure: MinimalDisclosure;
   filteredData: T[];
};

export function useSearchContext<T extends HotSpotItem>() {
   const context = useContext(SearchContext);
   return context as SearchContextType<T>;
}

export function SearchContextProvider<T extends HotSpotItem>({
   initialSearchText,
   children,
   searchDataPoint,
}: PropsWithChildren<SearchContextProps<T>>) {
   const dataContext = useDataContext<T>();

   const [searchState, setSearchState] = useState(initialSearchText || '');

   const showByDefault = Boolean(initialSearchText);
   const { isOpen, onOpen, onToggle, onClose } = useDisclosure({ defaultIsOpen: showByDefault });

   const filteredData = useMemo(() => {
      const groupedData = dataContext.data.flatMap(datapoint => datapoint.items);
      const sortedData = sortBy('description', groupedData);
      return filterData(sortedData, searchState, searchDataPoint);
   }, [searchState, dataContext.data]);

   const searchContext = {
      searchText: searchState,
      setSearchText: setSearchState,
      disclosure: {
         isOpen,
         onOpen,
         onClose,
         onToggle,
      },
      filteredData,
   };

   return <SearchContext.Provider value={searchContext}>{children}</SearchContext.Provider>;
}
